import { Command } from '@phosphor-icons/react/ssr';
import type { ComponentProps, ElementType, ReactNode } from 'react';

import { tv } from '../../lib/tv';

export type KbdProps = Omit<ComponentProps<'kbd'>, 'children'> & {
  /** Figma "Size": Small 20 (default, Caption) · Medium 24 (Label/Small). */
  size?: 'sm' | 'md';
  /**
   * Figma Type=Combo: the modifier key before the key. `'command'` draws
   * Icon/command (⌘ - Plus Jakarta Sans has no ⌘ glyph); any other string
   * ("Ctrl", "Shift") is drawn as text (Figma "Modifier as text").
   */
  modifier?: 'command' | (string & {});
  /**
   * Figma Type=Icon (e.g. ArrowUp): the key is drawn as this icon and
   * `children` becomes its screen-reader text ("Up arrow").
   */
  icon?: ElementType<{ className?: string }>;
  /** Figma "Key": the key label, e.g. "K". */
  children: ReactNode;
};

/**
 * One key cap. 1px border/default stroke with a 2px bottom edge, bg/subtle,
 * text/secondary; min width = height (20 / 24). The strokes sit inside the
 * cap's layout box, so sides are stroke + padding: 6/7px. One icon size per
 * key size (Key icon and ⌘): 12 Small, 14 Medium.
 */
const kbd = tv({
  slots: {
    combo: 'inline-flex items-center gap-[3px] align-middle',
    key: [
      'inline-flex shrink-0 items-center justify-center rounded-xs font-sans align-middle',
      'bg-bg-subtle text-text-secondary border border-b-2 border-border-default pb-px',
    ],
    icon: 'shrink-0',
  },
  variants: {
    size: {
      sm: {
        key: 'text-caption h-5 min-w-5 px-1.25',
        icon: 'size-3',
      },
      md: {
        key: 'text-label-sm h-6 min-w-6 px-1.5',
        icon: 'size-3.5',
      },
    },
  },
});

/**
 * Keyboard shortcut hint (Figma: Components ↳ Controls ↳ Kbd). Also expose
 * the shortcut on the control itself with `aria-keyshortcuts`.
 *
 *   <Kbd>K</Kbd> · <Kbd modifier="command">K</Kbd> · <Kbd modifier="Ctrl">K</Kbd>
 *   <Kbd icon={ArrowUp}>Up arrow</Kbd>
 */
export function Kbd({
  size = 'sm',
  modifier,
  icon: Icon,
  className,
  children,
  ...props
}: KbdProps) {
  const styles = kbd({ size });
  const key = (content: ReactNode, keyClass?: string) => (
    <kbd data-slot="kbd-key" className={styles.key({ className: keyClass })}>
      {content}
    </kbd>
  );
  const main = Icon ? (
    <>
      <Icon aria-hidden className={styles.icon()} />
      <span className="sr-only">{children}</span>
    </>
  ) : (
    children
  );

  if (modifier === undefined)
    return (
      <kbd data-slot="kbd-key" className={styles.key({ className })} {...props}>
        {main}
      </kbd>
    );

  return (
    <kbd data-slot="kbd" className={styles.combo({ className })} {...props}>
      {key(
        modifier === 'command' ? (
          <>
            <Command aria-hidden className={styles.icon()} />
            <span className="sr-only">Command</span>
          </>
        ) : (
          modifier
        ),
      )}
      {key(main)}
    </kbd>
  );
}
